/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Signed-in shell: tab bar from the URL (/search, /milemaker, /setup, …).
 * Tabs hide or disable based on subscription tier; Admin is email-gated.
 * Panels are lazy-loaded so maps/charts are not in the first bundle.
 */

import React, {lazy, Suspense, useEffect, useState} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {isMobile} from "react-device-detect";
import {useLocation, useNavigate} from "react-router-dom";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import "react-tabs/style/react-tabs.css";

import {PageLayout} from "../components/PageLayout";
import {PageLayoutMobile} from "../components/PageLayoutMobile";
import {PageLoader} from "../components/PageLoader";
import UserService from "../services/UserService";
import { getAdminUsers } from "../config/adminUsers";
import {
    getVisibleDashboardTabs,
    isDashboardTabDisabled,
    matchDashboardTabId,
    resolveDashboardLocation,
} from "../config/dashboardTabs";
import { ROUTES } from "../config/routes";
import { PAID_SUBSCRIPTION_TYPES, logoutToShop } from "../utils/subscriptionGate";
import '../styles/Dashboard.css';
import '../styles/TopLanes.css';

const FreightLaneManager = lazy(() => import("./FreightLaneManager"));
const PlanRoute = lazy(() => import("../components/milemaker/PlanRoute"));
const SearchTabs = lazy(() => import("./SearchTabs"));
const TopLanes = lazy(() => import("../components/TopLanes"));
const Insights = lazy(() => import("./Insights"));
const FileUploadProcessor = lazy(() => import("./LaneDataFileUpload"));
const Profile = lazy(() => import("./Profile"));
const Administration = lazy(() => import("./Administration"));
const RightDashboard = lazy(() => import("./RightDashboard"));
const CitySearchMobile = lazy(() => import("../components/CitySearchMobile"));

const adminUsers = getAdminUsers();

const TAB_PANELS = {
    home: () => <FreightLaneManager/>,
    milemaker: () => <PlanRoute/>,
    search: (subscriptionType) => <SearchTabs subscriptionType={subscriptionType}/>,
    'top-rates': () => <TopLanes/>,
    insights: () => <Insights/>,
    rfq: () => <FileUploadProcessor/>,
    profile: (subscriptionType, onDataChange) => <Profile onDataChange={onDataChange}/>,
    admin: () => <Administration/>,
    news: () => <RightDashboard/>,
};

const Dashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [subscriptionType, setSubscriptionType] = useState("Default");
    const [apiLoaded, setApiLoaded] = useState(false);
    const [onboardingStatus, setOnboardingStatus] = useState({
        profileIncomplete: false,
        companyIdIncomplete: false,
        loading: true
    });
    const {user, isAuthenticated, getAccessTokenSilently, isLoading: authLoading, logout} = useAuth0();
    const isAdmin = isAuthenticated && adminUsers.includes(user?.email);
    const visibleTabs = getVisibleDashboardTabs(isAdmin);
    const currentTabId = matchDashboardTabId(location.pathname);
    const selectedIndex = Math.max(0, visibleTabs.findIndex((tab) => tab.id === currentTabId));

    const handleProfileDataChange = () => {
        fetchSubscriptionType();
        fetchOnboardingStatus();
    };

    const fetchOnboardingStatus = async () => {
        if (authLoading || !isAuthenticated || !user) {
            return;
        }

        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.userSettingsFlow(token, user);
            const data = response?.data;

            setOnboardingStatus({
                profileIncomplete: data?.screenOne === true,
                companyIdIncomplete: data?.screenTwo === true,
                loading: false
            });
        } catch (error) {
            console.error("Error fetching onboarding status:", error);
            setOnboardingStatus({
                profileIncomplete: false,
                companyIdIncomplete: false,
                loading: false
            });
        }
    };

    const fetchSubscriptionType = async () => {
        if (authLoading || !isAuthenticated || !user) {
            return;
        }

        setApiLoaded(false);
        try {
            const token = await getAccessTokenSilently();
            let subType = "Default";
            for (let attempt = 0; attempt < 4; attempt++) {
                const response = await UserService.verify(user, token);
                subType = response?.data?.subscriptionType || "Default";
                if (PAID_SUBSCRIPTION_TYPES.includes(subType)) {
                    break;
                }
                if (attempt < 3) {
                    await new Promise((resolve) => setTimeout(resolve, 1500));
                }
            }
            if (!PAID_SUBSCRIPTION_TYPES.includes(subType)) {
                logoutToShop(logout);
                return;
            }
            setSubscriptionType(subType);
            setApiLoaded(true);
        } catch (error) {
            console.error("Error fetching subscription type", error);
            if (error?.error === 'login_required') {
                throw error;
            }
            logoutToShop(logout);
        }
    };

    useEffect(() => {
        if (!authLoading && isAuthenticated && user) {
            fetchSubscriptionType();
            fetchOnboardingStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isAuthenticated, user]);

    useEffect(() => {
        if (!apiLoaded) {
            return;
        }
        const resolved = resolveDashboardLocation({
            pathname: location.pathname,
            search: location.search,
            subscriptionType,
            isAdmin,
        });
        if (resolved.redirectTo && resolved.redirectTo !== location.pathname) {
            navigate(resolved.redirectTo, { replace: true });
        }
    }, [apiLoaded, isAdmin, location.pathname, location.search, navigate, subscriptionType]);

    useEffect(() => {
        const handleNavigateToProfile = () => {
            navigate(ROUTES.DASHBOARD_PROFILE);
        };

        const handleOnboardingStatusChanged = () => {
            fetchOnboardingStatus();
        };

        window.addEventListener('navigateToProfile', handleNavigateToProfile);
        window.addEventListener('onboardingStatusChanged', handleOnboardingStatusChanged);

        return () => {
            window.removeEventListener('navigateToProfile', handleNavigateToProfile);
            window.removeEventListener('onboardingStatusChanged', handleOnboardingStatusChanged);
        };
    }, [navigate]);

    if (isMobile) {
        return (
            <PageLayoutMobile>
                <Suspense fallback={<PageLoader/>}>
                    <CitySearchMobile/>
                </Suspense>
            </PageLayoutMobile>
        );
    }

    void onboardingStatus;

    return (
        <div>
            <PageLayout>
                {apiLoaded && (
                        <div className="dashboard-container">
                            <div className="row">
                                <div className="col-md-12">

                                    <Tabs
                                        selectedIndex={selectedIndex}
                                        onSelect={(index) => {
                                            const tab = visibleTabs[index];
                                            if (!tab || isDashboardTabDisabled(tab.disableKey, subscriptionType)) {
                                                return;
                                            }
                                            if (tab.path !== location.pathname) {
                                                navigate(tab.path);
                                            }
                                        }}
                                    >
                                    <TabList className="profile-root-tabs">
                                        {visibleTabs.map((tab) => (
                                            <Tab
                                                key={tab.id}
                                                disabled={isDashboardTabDisabled(tab.disableKey, subscriptionType)}
                                                title={undefined}
                                            >
                                                {tab.tooltip ? (
                                                    <span data-toggle="tooltip" data-placement="top" title={tab.tooltip}>
                                                        {tab.label}
                                                    </span>
                                                ) : tab.label}
                                            </Tab>
                                        ))}
                                    </TabList>

                                    {visibleTabs.map((tab) => (
                                        <TabPanel key={tab.id} {...(tab.panelClassName ? { className: tab.panelClassName } : {})}>
                                            <Suspense fallback={<PageLoader/>}>
                                                {TAB_PANELS[tab.id](subscriptionType, handleProfileDataChange)}
                                            </Suspense>
                                        </TabPanel>
                                    ))}
                                </Tabs>
                                </div>
                            </div>
                        </div>
                )}
            </PageLayout>
        </div>
    );
};

export default Dashboard;
